import { createBoard } from "../go/rules";
import type { GradeLabel, MoveGrade } from "../engine/types";
import {
  BLACK,
  EMPTY,
  WHITE,
  type Board,
  type Color,
  type EvalSummary,
  type MoveRecord,
  type Point,
  type PositionSnapshot,
  type Stone
} from "../go/types";

export type PersistedTool = "play" | "black" | "white" | "erase";
export type PersistedGameMode = "study" | "play-ai";

export interface PersistedSession {
  boardSize: number;
  current: PositionSnapshot;
  past: PositionSnapshot[];
  future: PositionSnapshot[];
  tool: PersistedTool;
  komi: number;
  thinkSeconds: number;
  autoAnalyze: boolean;
  gameMode: PersistedGameMode;
  humanColor: Color;
}

const STORAGE_KEY = "gocoach.session.v1";
const SUPPORTED_BOARD_SIZES = [19, 13, 9];
const DEFAULT_BOARD_SIZE = 13;
const DEFAULT_THINK_SECONDS = 3.5;
const DEFAULT_KOMI = 7.5;
const MIN_THINK_SECONDS = 2;
const MAX_THINK_SECONDS = 15;
const MAX_HISTORY_ITEMS = 300;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function color(value: unknown): Color | null {
  return value === "black" || value === "white" ? value : null;
}

function tool(value: unknown): PersistedTool | null {
  return value === "play" || value === "black" || value === "white" || value === "erase" ? value : null;
}

function gameMode(value: unknown): PersistedGameMode {
  return value === "play-ai" ? "play-ai" : "study";
}

function point(value: unknown, size: number): Point | null {
  if (!isRecord(value)) return null;
  const x = Number(value.x);
  const y = Number(value.y);
  if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= size || y >= size) {
    return null;
  }
  return { x, y };
}

function board(value: unknown, size: number): Board | null {
  if (!Array.isArray(value) || value.length !== size * size) {
    return null;
  }

  const next: Stone[] = [];
  for (const item of value) {
    if (item !== EMPTY && item !== BLACK && item !== WHITE) {
      return null;
    }
    next.push(item);
  }
  return next;
}

const GRADE_LABELS: GradeLabel[] = ["Great", "Good", "Inaccuracy", "Mistake", "Blunder"];

function moveGrade(value: unknown): MoveGrade | undefined {
  if (!isRecord(value) || !GRADE_LABELS.includes(value.label as GradeLabel)) {
    return undefined;
  }
  const pointLoss = Number(value.pointLoss);
  const winrateDrop = Number(value.winrateDrop);
  if (!Number.isFinite(pointLoss) || !Number.isFinite(winrateDrop) || typeof value.bestLabel !== "string") {
    return undefined;
  }
  const bestPv = Array.isArray(value.bestPv) && value.bestPv.every((item) => typeof item === "string")
    ? (value.bestPv as string[])
    : undefined;
  return { label: value.label as GradeLabel, pointLoss, winrateDrop, bestLabel: value.bestLabel, bestPv };
}

function evalSummary(value: unknown): EvalSummary | undefined {
  if (!isRecord(value)) return undefined;
  const winrateBlack = Number(value.winrateBlack);
  const scoreLeadBlack = Number(value.scoreLeadBlack);
  if (!Number.isFinite(winrateBlack) || !Number.isFinite(scoreLeadBlack) || typeof value.bestLabel !== "string") {
    return undefined;
  }
  return { winrateBlack, scoreLeadBlack, bestLabel: value.bestLabel };
}

function moveRecord(value: unknown, size: number): MoveRecord | null {
  if (value === null) return null;
  if (!isRecord(value)) return null;

  const moveColor = color(value.color);
  if (!moveColor || typeof value.label !== "string" || typeof value.pass !== "boolean") {
    return null;
  }

  const movePoint = value.point === null ? null : point(value.point, size);
  if (!value.pass && !movePoint) {
    return null;
  }

  const captures = Number(value.captures ?? 0);

  return {
    color: moveColor,
    point: movePoint,
    label: value.label,
    pass: value.pass,
    captures: Number.isFinite(captures) ? Math.max(0, captures) : 0,
    grade: moveGrade(value.grade)
  };
}

function snapshot(value: unknown, size: number): PositionSnapshot | null {
  if (!isRecord(value)) return null;

  const snapshotBoard = board(value.board, size);
  const toPlay = color(value.toPlay);
  const move = moveRecord(value.move, size);
  if (!snapshotBoard || !toPlay) {
    return null;
  }

  return {
    board: snapshotBoard,
    toPlay,
    move,
    evalSummary: evalSummary(value.evalSummary)
  };
}

function snapshots(value: unknown, size: number): PositionSnapshot[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  const parsed = value.slice(-MAX_HISTORY_ITEMS).map((item) => snapshot(item, size));
  if (parsed.some((item) => item === null)) {
    return null;
  }
  return parsed as PositionSnapshot[];
}

function numberInRange(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, parsed));
}

export function emptySession(size = DEFAULT_BOARD_SIZE): PersistedSession {
  return {
    boardSize: size,
    current: {
      board: createBoard(size),
      toPlay: "black",
      move: null
    },
    past: [],
    future: [],
    tool: "play",
    komi: DEFAULT_KOMI,
    thinkSeconds: DEFAULT_THINK_SECONDS,
    autoAnalyze: false,
    gameMode: "study",
    humanColor: "black"
  };
}

export function parseSession(raw: string | null): PersistedSession | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }

    const size = Number(parsed.boardSize);
    if (!SUPPORTED_BOARD_SIZES.includes(size)) {
      return null;
    }

    const current = snapshot(parsed.current, size);
    const past = snapshots(parsed.past, size);
    const future = snapshots(parsed.future, size);
    if (!current || !past || !future) {
      return null;
    }

    return {
      boardSize: size,
      current,
      past,
      future,
      tool: tool(parsed.tool) ?? "play",
      komi: numberInRange(parsed.komi, DEFAULT_KOMI, -50, 50),
      thinkSeconds: numberInRange(parsed.thinkSeconds, DEFAULT_THINK_SECONDS, MIN_THINK_SECONDS, MAX_THINK_SECONDS),
      autoAnalyze: Boolean(parsed.autoAnalyze),
      gameMode: gameMode(parsed.gameMode),
      humanColor: color(parsed.humanColor) ?? "black"
    };
  } catch {
    return null;
  }
}

export function loadSession(storage: Pick<Storage, "getItem"> = window.localStorage): PersistedSession {
  try {
    return parseSession(storage.getItem(STORAGE_KEY)) ?? emptySession();
  } catch {
    return emptySession();
  }
}

export function saveSession(session: PersistedSession, storage: Pick<Storage, "setItem"> = window.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ ...session, savedAt: new Date().toISOString() }));
  } catch {
    // Storage can fail in private mode or under quota pressure; the app remains usable without persistence.
  }
}

export function clearSession(storage: Pick<Storage, "removeItem"> = window.localStorage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures; clearing is best-effort.
  }
}
